import React, { useContext, useEffect } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Form, Grid, Icon, Message, Segment } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { CoolFarmCropOptionsContext } from '../..'
import { DateInput } from 'semantic-ui-calendar-react'
import { useAuth } from '~/routes/components/AuthProvider'
import NPKSlider from './NPKSlider'
import { StyledColumn, StyledMessage } from './style'
import InfoPopup from '~/components/InfoPopup'
import dayjs from 'dayjs'

interface Props {
  index: number
}

export const CUSTOM_FERTILIZER_ID = 44

const defaultCustomFertFields = {
  nTotal: '',
  nAmmonia: '',
  nNitric: '',
  nUrea: '',
  pTotal: '',
  pType: '',
  kTotal: '',
  kType: ''
}

const Fertilizer: React.FC<Props> = ({ index }) => {
  const { t } = useTranslation()
  const { control, setValue, getValues, formState:{ errors } } = useFormContext()
  const options = useContext(CoolFarmCropOptionsContext)
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)

  const watchFertilizerId = useWatch({ control, name: `fertilizers[${index}].id` })

  const watchNTotal = useWatch({ control, name: `fertilizers[${index}].nTotal` })
  const watchNAmmonia = useWatch({ control, name: `fertilizers[${index}].nAmmonia` })
  const watchNNitric = useWatch({ control, name: `fertilizers[${index}].nNitric` })
  const watchNUrea = useWatch({ control, name: `fertilizers[${index}].nUrea` })
  const watchHarvestingYear = useWatch({ control, name: 'harvestingYear' })

  const calculateNitrogenValidation = () => {
    const nTotal = parseFloat(watchNTotal) || 0
    const nAmmonia = parseFloat(watchNAmmonia) || 0
    const nNitric = parseFloat(watchNNitric) || 0
    const nUrea = parseFloat(watchNUrea) || 0
    const sum = nAmmonia + nNitric + nUrea
    const isValid = Math.abs(nTotal - sum) < 0.01
    return { nTotal, sum, isValid, nAmmonia, nNitric, nUrea }
  }

  const nitrogenValidation = watchFertilizerId === CUSTOM_FERTILIZER_ID ? calculateNitrogenValidation() : null
  const isNitrogenValid = nitrogenValidation?.isValid

  useEffect(() => {
    if (watchFertilizerId !== CUSTOM_FERTILIZER_ID) {
      const fertilizer = getValues(`fertilizers[${index}]`)
      setValue(`fertilizers[${index}]`, { ...fertilizer, ...defaultCustomFertFields })
    }
  }, [watchFertilizerId, index])

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  const fertilizerOptions = options?.fertilizers?.map(fert => ({
    key: fert.id,
    value: fert.id,
    text: fert.label
  })) || []

  const amountUnitOptions = options?.fertilizerRateUnits?.map(unit => ({
    key: unit.key,
    value: unit.key.toString(),
    text: unit.label
  })) || []

  const applicationMethodOptions = options?.fertilizerApplicationMethods?.map(method => ({
    key: method.key,
    value: method.key,
    text: method.label
  })) || []

  const emissionsInhibitorsOptions = options?.fertilizerEmissionsInhibitors?.map(type => ({
    key: type.key,
    value: type.key,
    text: type.label
  })) || []

  const pTypeOptions = options?.fertilizerPTypes?.map(type => ({
    key: type.key,
    value: type.key,
    text: type.label
  })) || []

  const kTypeOptions = options?.fertilizerKTypes?.map(type => ({
    key: type.key,
    value: type.key,
    text: type.label
  })) || []

  function renderCustomNpk() {
    const sumOfNitrogens = `${nitrogenValidation?.nNitric?.toFixed(1)}% + ${nitrogenValidation?.nAmmonia?.toFixed(1)}% + ${nitrogenValidation?.nUrea?.toFixed(1)}% = ${nitrogenValidation?.sum?.toFixed(1)}%`
    return (
      <Grid.Row>
        <Grid.Column width={16}>
          <Segment style={{ backgroundColor: '#f9f9f9', padding: '1.5em' }}>
            <StyledMessage $isSuccess={isNitrogenValid}>
              <span style={{ fontWeight: 'bold', color: isNitrogenValid ? '#155724' : '#ff0707ff' }}>
                {isNitrogenValid ? <Icon name={'checkmark'} style={{ fontSize: '2em' }}/> : <Icon name='warning sign' style={{ fontSize: '2em' }}/>}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', marginBottom: '0.2em', color: isNitrogenValid ? '#155724' : '#ff0707ff' }}>
                  {t('Total % N')} = {t('% Nitrate')} + {t('% Ammonium')} + {t('% Urea')}
                </div>
                <div style={{ fontSize: '0.9em', color: isNitrogenValid ? '#155724' : '#ff0707ff' }}>
                  {nitrogenValidation?.nTotal?.toFixed(1)}% {isNitrogenValid ? '=' : '≠'} <span>{sumOfNitrogens}</span>
                </div>
              </div>
            </StyledMessage>
            <Grid>
              <Grid.Row>
                <Grid.Column width={8}>
                  <NPKSlider
                    name='nTotal'
                    label='% N'
                    index={index}
                    />
                </Grid.Column>
                <Grid.Column width={8}>
                  <NPKSlider
                    name='nAmmonia'
                    label='% N (as ammonium-N)'
                    index={index}
                    />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={8}>
                  <NPKSlider
                    name='nNitric'
                    label='% N (as nitrate-N)'
                    index={index}
                    />
                </Grid.Column>
                <Grid.Column width={8}>
                  <NPKSlider
                    name='nUrea'
                    label='% N (as urea-N)'
                    index={index}
                    />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={8}>
                  <Grid columns={2}>
                    <Grid.Row style={{ alignItems: 'anchor-center' }}>
                      <Grid.Column width={10}>
                        <NPKSlider
                          name='pTotal'
                          label='<span>% P<sub>2</sub>O<sub>5</sub> or % P</span>'
                          index={index}
                          />
                      </Grid.Column>

                      <StyledColumn width={4}>
                        <FormFields.Select
                          control={control}
                          name={`fertilizers[${index}].pType`}
                          rules={{
                            required: true
                          }}
                          withoutAsterisk
                          options={pTypeOptions}
                          placeholder={t('Select P type')}
                          noErrorMessage
                          />
                      </StyledColumn>
                    </Grid.Row>
                  </Grid>
                </Grid.Column>
                <Grid.Column width={8}>
                  <Grid columns={2}>
                    <Grid.Row style={{ alignItems: 'anchor-center' }}>
                      <Grid.Column width={10}>
                        <NPKSlider
                          name='kTotal'
                          label='<span>% K<sub>2</sub>O or % K</span>'
                          index={index}
                          />
                      </Grid.Column>
                      <StyledColumn width={4}>
                        <FormFields.Select
                          control={control}
                          size={'small'}
                          name={`fertilizers[${index}].kType`}
                          withoutAsterisk
                          rules={{
                            required: true
                          }}
                          options={kTypeOptions}
                          placeholder={t('Select K type')}
                          noErrorMessage
                          />
                      </StyledColumn>
                    </Grid.Row>
                  </Grid>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Segment>
        </Grid.Column>
      </Grid.Row>
    )
  }

  const fertilizerSelected = options?.fertilizers?.find(fert => fert.id === watchFertilizerId)
  const minFertilizerDate = dayjs(`01.08.${Number(watchHarvestingYear) - 1}`, 'DD.MM.YYYY').format('DD.MM.YYYY');
  const maxFertilizerDate = dayjs(`31.12.${watchHarvestingYear}`, 'DD.MM.YYYY').format('DD.MM.YYYY');
  const displayRegion = !watchFertilizerId ?
    true :
    fertilizerSelected?.productionRegions?.length > 0

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column width={8}>
          <Form.Field>
            <label>
              {t('Fertiliser type')}
            </label>
            <FormFields.Select
              control={control}
              search
              rules={{
                required: true
              }}
              name={`fertilizers[${index}].id`}
              withoutAsterisk
              options={fertilizerOptions}
              placeholder={t('Select fertilizer')}
              noErrorMessage
            />
          </Form.Field>
        </Grid.Column>
        <Grid.Column width={8}>
          <Form.Field>
            <label style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              {t('Application rate')}
              <InfoPopup
                content={t('Enter the rate per unit area. If you apply a variable rate, take the average for the whole field in your assessment.')}
                styleIcon={{ fontSize: '1.2em' }}
              />
            </label>
            <div style={{ display: 'flex', gap: '0.5em' }}>
              <div style={{ flex: 2 }}>
                <FormFields.Input
                  control={control}
                  rules={{
                    required: true
                  }}
                  name={`fertilizers[${index}].amount`}
                  withoutAsterisk
                  placeholder={t('Amount')}
                  typeInput='controled'
                  noErrorMessage
                />
              </div>
              <div style={{ flex: 1 }}>
                <FormFields.Select
                  control={control}
                  name={`fertilizers[${index}].amountUnit`}
                  withoutLabel
                  rules={{
                    required: true
                  }}
                  options={amountUnitOptions}
                  placeholder={t('Unit')}
                  noErrorMessage
                />
              </div>
            </div>
          </Form.Field>
        </Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={8}>
          {displayRegion && <Form.Field>
            <label style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              {t('Manufactured in')}
              <InfoPopup
                content={t('Choose the manufacturing region of the fertiliser you are applying. The year relates to when the emissions factor was updated.')}
                styleIcon={{ fontSize: '1.2em' }}
              />
            </label>
            <FormFields.Select
              options={fertilizerSelected?.productionRegions?.map(region => ({ value: region.id, text: region.label })) || []}
              disabled={!watchFertilizerId}
              control={control}
              rules={{
                required: true
              }}
              name={`fertilizers[${index}].cftProductionRegionId`}
              withoutAsterisk
              placeholder={t('Region')}
              noErrorMessage
            />
          </Form.Field>}
        </Grid.Column>
        {watchFertilizerId !== CUSTOM_FERTILIZER_ID &&
          <Grid.Column width={8}>
            <Form.Field>
              <label>{t('Emissions inhibitors')}</label>
              <FormFields.Select
                control={control}
                name={`fertilizers[${index}].emissionsInhibitors`}
                withoutAsterisk
                rules={{
                  required: true
                }}
                options={emissionsInhibitorsOptions}
                placeholder={t('Select')}
                noErrorMessage
            />
            </Form.Field>
          </Grid.Column>}
        {/*  <Grid.Column width={8}>
          <Form.Field>
            <label style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              {t('Fertiliser weight, or units?')}
              <InfoPopup
                content={<div dangerouslySetInnerHTML={{ __html: t(`Specify either the weight of whole fertiliser product, or only the weight of the active element (e.g. units of P<sub>2</sub>O<sub>5</sub>, K<sub>2</sub>0, or N).
                  For example: 100 kg/ha of Urea (46% N) product could also be entered as 46 kg/ha of N.`) }}/>}
                styleIcon={{ fontSize: '1.2em' }}
              />
            </label>
            <FormFields.Select
              control={control}
              options={fertilizerSelected?.ingredients?.map(region => ({ value: region.id, text: region.label })) || []}
              name={`fertilizers[${index}].cftIngredientId`}
              disabled={!watchFertilizerId}
              withoutAsterisk
              rules={{
                required: true
              }}
              placeholder={t('Select')}
              noErrorMessage
            />
          </Form.Field>
        </Grid.Column> */}
      </Grid.Row>

      {watchFertilizerId === CUSTOM_FERTILIZER_ID && renderCustomNpk()}

      <Grid.Row>
        <Grid.Column width={8}>
          <Form.Field>
            <label>{t('Application method')}</label>
            <FormFields.Select
              control={control}
              name={`fertilizers[${index}].applicationMethod`}
              withoutAsterisk
              options={applicationMethodOptions}
              rules={{
                required: true
              }}
              placeholder={t('Method')}
              noErrorMessage
            />
          </Form.Field>
        </Grid.Column>
        <Grid.Column width={8}>
          <Form.Field>
            <label>{t('Date of application')}</label>
            <Controller
              control={control}
              name={`fertilizers[${index}].applicationDate`}
              rules={{
                required: true
              }}
              render={({ field, fieldState }) => {
                return <Form.Field
                  required
                  error={Boolean(fieldState.error)}
                >
                  <DateInput
                    name='dateTime'
                    className={'datePicker'}
                    closable
                    markColor={'blue'}
                    onKeyDown={handleKeyPress}
                    minDate={minFertilizerDate}
                    initialDate={minFertilizerDate}
                    maxDate={maxFertilizerDate}
                    disabled={!watchHarvestingYear}
                    onKeyPress={handleKeyPress}
                    localization={languageLocal.code}
                    closeOnMouseLeave={false}
                    dateFormat={'DD.MM.YYYY'}
                    placeholder={'DD.MM.YYYY'}
                    value={field.value}
                    iconPosition='left'
                    onChange={(event, { name, value }) => setValue(`fertilizers[${index}].applicationDate`, value)}
                  />
                </Form.Field>
              }}
            />
          </Form.Field>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}
export default Fertilizer